import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { BookOpen, LogOut, Loader2, FileQuestion } from "lucide-react";
import ContentEditorTab from "../components/admin/tabs/ContentEditorTab";
import QuestionBankAdmin from "./QuestionBankAdmin";

export default function ContentManagerEditor() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("editor"); // editor | questions

  const fetchMe = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/auth/me`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.admin && data.admin.role === "content_manager") {
        setMe(data.admin);
      } else if (res.ok && data.admin) {
        // Admins/super admins manage content in the admin console
        navigate("/admin/content-editor");
      } else {
        navigate("/contentmanager");
      }
    } catch {
      navigate("/contentmanager");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/admin/auth/logout`, { method: "POST", credentials: "include" });
    } catch {
      /* ignore */
    }
    toast.success("Logged out");
    navigate("/contentmanager");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }
  if (!me) return null;

  const scope = me.content_scope || { grades: [], subjects: [] };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800 leading-tight">Content Manager</h1>
              <p className="text-xs text-slate-500">{me.name || me.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-1">
              {(scope.grades || []).map((g) => (
                <Badge key={`g${g}`} variant="secondary" data-testid={`scope-grade-${g}`}>Class {g}</Badge>
              ))}
              {(scope.subjects || []).map((s) => (
                <Badge key={`s${s}`} className="bg-indigo-100 text-indigo-700 capitalize" data-testid={`scope-subject-${s}`}>{s}</Badge>
              ))}
            </div>
            <Button variant="ghost" className="text-red-600 hover:bg-red-50" onClick={handleLogout} data-testid="cm-logout-btn">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center gap-2 mb-6">
          <Button
            variant={tab === "editor" ? "default" : "outline"}
            size="sm"
            onClick={() => setTab("editor")}
            data-testid="cm-tab-editor"
          >
            <BookOpen className="h-4 w-4 mr-1.5" /> Content Editor
          </Button>
          <Button
            variant={tab === "questions" ? "default" : "outline"}
            size="sm"
            onClick={() => setTab("questions")}
            data-testid="cm-tab-questions"
          >
            <FileQuestion className="h-4 w-4 mr-1.5" /> Question Bank
          </Button>
        </div>
        {tab === "editor" ? (
          <ContentEditorTab isContentManager scope={scope} />
        ) : (
          <QuestionBankAdmin />
        )}
      </main>
    </div>
  );
}
